/**
 * Imperative toast API on top of @hessianhq/ui `Toast` + `ToastDock`.
 *
 * @hessianhq/ui ships the visual components but not a global imperative
 * trigger (the way svelte-sonner does with `toast.success(…)`). This
 * shim closes that gap: a module-level `$state` queue plus
 * `toast.success / error / info / warning` helpers that push, schedule
 * auto-dismissal, and expose `dismiss(id)`. The root layout renders a
 * single <ToastDock> + iterates this queue, so any callsite can fire a
 * toast without owning the markup.
 */

let nextId = 0;

export type ToastVariant = 'default' | 'success' | 'danger' | 'warn' | 'info';

export type ToastOptions = {
	/** Secondary line shown beneath `message`. */
	description?: string;
	/**
	 * Milliseconds before auto-dismissal. Defaults to 4000.
	 * Set to 0 (or `Infinity`) to make the toast sticky until clicked.
	 */
	duration?: number;
};

export type ToastEntry = {
	id: number;
	variant: ToastVariant;
	message: string;
	description?: string;
	duration: number;
};

const DEFAULT_DURATION_MS = 4000;

// Module-level reactive queue. `$state` works at module scope inside
// `.svelte.ts`, so every component that imports this array sees mutations
// automatically — that's what lets <ToastDock> in `+layout.svelte` re-render
// whenever a far-away callsite fires a toast.
export const toasts = $state<ToastEntry[]>([]);

const timers = new Map<number, ReturnType<typeof setTimeout>>();

function push(variant: ToastVariant, message: string, options: ToastOptions = {}) {
	const id = ++nextId;
	const duration = options.duration ?? DEFAULT_DURATION_MS;
	toasts.push({
		id,
		variant,
		message,
		description: options.description,
		duration
	});
	// Finite, positive duration → schedule auto-dismiss. 0 / Infinity keeps
	// the toast sticky until the user clicks the close glyph (Toast emits
	// `onClose`, which we wire to `dismiss(id)` in the dock).
	if (Number.isFinite(duration) && duration > 0) {
		timers.set(
			id,
			setTimeout(() => dismiss(id), duration)
		);
	}
	return id;
}

export function dismiss(id: number) {
	const idx = toasts.findIndex((t) => t.id === id);
	if (idx !== -1) toasts.splice(idx, 1);
	const timer = timers.get(id);
	if (timer) {
		clearTimeout(timer);
		timers.delete(id);
	}
}

export const toast = {
	/**
	 * Neutral acknowledgment — copy-to-clipboard, refresh, share-link, etc.
	 * Use this for any popup that isn't really reporting a success/failure
	 * outcome of a mutating operation.
	 */
	default: (message: string, options?: ToastOptions) => push('default', message, options),
	success: (message: string, options?: ToastOptions) => push('success', message, options),
	error: (message: string, options?: ToastOptions) => push('danger', message, options),
	warning: (message: string, options?: ToastOptions) => push('warn', message, options),
	info: (message: string, options?: ToastOptions) => push('info', message, options),
	dismiss
};
